import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Clock3,
  CloudOff,
  Coins,
  Landmark,
  Loader2,
  Receipt,
  ShieldAlert,
  Ticket,
  Trophy,
  Wallet,
} from "lucide-react";
import { fetchHouseTreasury } from "../../redux/operatorConsoleSlice";
import {
  formatCash,
  ledgerLabel,
  LEDGER_TONE,
} from "../../utils/operatorConsoleHelpers";
import { formatAppDateTime } from "../../utils/appTimeFormat";
import "../../operator-finance.css";

const VIEW_W = 640;
const VIEW_H = 210;
const PAD_TOP = 14;
const PAD_BOTTOM = 22;

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const compact = (value) => {
  const n = Number(value) || 0;
  const abs = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 10_000) return `${sign}${(abs / 1000).toFixed(0)}K`;
  if (abs >= 1000) return `${sign}${(abs / 1000).toFixed(1)}K`;
  return `${sign}${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const dayLabel = (dateKey) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateKey || ""));
  if (!match) return String(dateKey || "");
  const dt = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const MONEY_IN_TYPES = new Set(["DEPOSIT", "STAKE_RECEIVED"]);
const MONEY_OUT_TYPES = new Set([
  "WITHDRAWAL",
  "PAYOUT_MADE",
  "PLATFORM_COMMISSION",
]);
const INTERNAL_TYPES = new Set(["LIABILITY_LOCKED", "LIABILITY_RELEASED"]);

const LEDGER_FILTERS = [
  { id: "all", label: "All", type: null },
  { id: "deposits", label: "Deposits", type: "DEPOSIT" },
  { id: "withdrawals", label: "Withdrawals", type: "WITHDRAWAL" },
  { id: "stakes", label: "Stakes", type: "STAKE_RECEIVED" },
  { id: "payouts", label: "Payouts", type: "PAYOUT_MADE" },
  { id: "commission", label: "Commission", type: "PLATFORM_COMMISSION" },
  // No single backend type covers both liability moves; fetch unfiltered
  // pages and narrow client-side.
  { id: "liability", label: "Liability", type: null, clientTypes: INTERNAL_TYPES },
];

const LEDGER_ICONS = {
  DEPOSIT: Wallet,
  WITHDRAWAL: ArrowUpRight,
  STAKE_RECEIVED: Ticket,
  PAYOUT_MADE: Trophy,
  PLATFORM_COMMISSION: Receipt,
  LIABILITY_LOCKED: ShieldAlert,
  LIABILITY_RELEASED: ShieldAlert,
};

/* ─────────────────────── Vault hero ─────────────────────── */

const VaultHero = ({ summary, flowWindow }) => {
  const available = Number(summary.availableBalance) || 0;
  const locked = Number(summary.lockedLiability) || 0;
  const pending = Number(summary.pendingBalanceBook) || 0;
  const ownerProfit = Number(summary.ownerProfit) || 0;
  const ledgerAvailable = Number(summary.ledgerAvailableBalance) || 0;
  const threshold = Number(summary.lowBalanceThreshold) || 0;
  const isLow = Boolean(summary.isLowBalance);

  const total = available + locked + pending;
  const pct = (part) => (total > 0 ? Math.round((part / total) * 100) : 0);

  const segments = [
    { id: "available", label: "Available", value: available },
    { id: "locked", label: "Locked liability", value: locked },
    { id: "pending", label: "Pending", value: pending },
  ];

  // Runway meter: available vs the low-balance threshold. Scale the track so
  // the threshold marker sits inside it even when available is far above it.
  const scale = Math.max(available, threshold) * 1.15 || 1;
  const fillPct = clamp01(available / scale) * 100;
  const markerPct = clamp01(threshold / scale) * 100;
  const headroom = available - threshold;

  const bookDrift = Math.abs(available - ledgerAvailable);
  const reconciled = bookDrift < 0.01;

  return (
    <section className="oc-fin-card oc-fin-vault">
      <div className="oc-fin-vault-main">
        <span className="oc-fin-kicker">Treasury vault</span>
        <strong className="oc-fin-vault-value tabular-nums">
          {formatCash(total, { compact: true })}
        </strong>
        <span className="oc-fin-vault-sub">
          {reconciled
            ? `Book and ledger agree at ${compact(available)} available`
            : `Book ${compact(available)} vs ledger ${compact(ledgerAvailable)} available`}
        </span>
      </div>

      <div className="oc-fin-profit-callout">
        <Trophy size={14} />
        <div>
          <em>Owner profit</em>
          <strong className="tabular-nums">{formatCash(ownerProfit, { compact: true })}</strong>
        </div>
      </div>

      <div className="oc-fin-comp">
        <div className="oc-fin-comp-bar" aria-hidden>
          {segments.map((seg) =>
            seg.value > 0 ? (
              <span
                key={seg.id}
                className={`oc-fin-comp-seg oc-fin-comp-seg-${seg.id}`}
                style={{ width: `${total > 0 ? Math.max((seg.value / total) * 100, 1.5) : 0}%` }}
              />
            ) : null
          )}
        </div>
        <div className="oc-fin-comp-legend">
          {segments.map((seg) => (
            <span key={seg.id} className="oc-fin-comp-key">
              <span className={`oc-fin-comp-dot oc-fin-comp-dot-${seg.id}`} aria-hidden />
              {seg.label}
              <strong className="tabular-nums">{compact(seg.value)}</strong>
              <em>{pct(seg.value)}%</em>
            </span>
          ))}
        </div>
      </div>

      <div className={`oc-fin-threshold${isLow ? " oc-fin-threshold-warn" : ""}`}>
        <div className="oc-fin-threshold-meta">
          <span className="oc-fin-threshold-label">
            {isLow ? <AlertTriangle size={11} /> : <Landmark size={11} />}
            {isLow ? "Below low-balance threshold" : "Balance runway"}
          </span>
          <strong className={`tabular-nums ${isLow ? "oc-fin-down" : "oc-fin-up"}`}>
            {headroom >= 0
              ? `+${compact(headroom)} above threshold`
              : `${compact(headroom)} short`}
          </strong>
        </div>
        <div className="oc-fin-threshold-track" aria-hidden>
          <span
            className={`oc-fin-threshold-fill${isLow ? " oc-fin-threshold-fill-warn" : ""}`}
            style={{ width: `${fillPct}%` }}
          />
          {threshold > 0 ? (
            <span className="oc-fin-threshold-marker" style={{ left: `${markerPct}%` }} />
          ) : null}
        </div>
        <span className="oc-fin-threshold-hint">
          {threshold > 0
            ? `Threshold ${compact(threshold)} · available ${compact(available)}${
                isLow ? " — top up to keep tickets flowing" : ""
              }`
            : "No low-balance threshold configured for this house"}
        </span>
      </div>

      {flowWindow ? (
        <div className="oc-fin-tiles">
          <div className="oc-fin-tile">
            <span>Money in</span>
            <strong className="tabular-nums oc-fin-up">{compact(flowWindow.inflow)}</strong>
            <em>Deposits + stakes kept</em>
          </div>
          <div className="oc-fin-tile">
            <span>Money out</span>
            <strong className="tabular-nums oc-fin-down">{compact(flowWindow.outflow)}</strong>
            <em>Withdrawals, payouts, fees</em>
          </div>
          <div className="oc-fin-tile">
            <span>Net flow</span>
            <strong
              className={`tabular-nums ${
                flowWindow.net > 0 ? "oc-fin-up" : flowWindow.net < 0 ? "oc-fin-down" : ""
              }`}
            >
              {flowWindow.net > 0 ? "+" : ""}
              {compact(flowWindow.net)}
            </strong>
            <em>{flowWindow.spanLabel}</em>
          </div>
          <div className="oc-fin-tile">
            <span>Internal moves</span>
            <strong className="tabular-nums oc-fin-blue">{compact(flowWindow.internal)}</strong>
            <em>Liability locked / released</em>
          </div>
        </div>
      ) : null}
    </section>
  );
};

/* ─────────────────────── Cashflow chart ─────────────────────── */

const CashflowChart = ({ timeline }) => {
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const n = timeline.length;

  const { yMin, yMax } = useMemo(() => {
    let hi = 0;
    let lo = 0;
    for (const p of timeline) {
      hi = Math.max(hi, p.inflow);
      lo = Math.min(lo, -p.outflow);
    }
    if (hi === 0 && lo === 0) hi = 1;
    const pad = (hi - lo) * 0.08;
    return { yMin: lo === 0 ? 0 : lo - pad, yMax: hi === 0 ? 0 : hi + pad };
  }, [timeline]);

  const innerH = VIEW_H - PAD_TOP - PAD_BOTTOM;
  const span = Math.max(yMax - yMin, 1e-9);
  const toY = useCallback(
    (v) => PAD_TOP + (1 - (v - yMin) / span) * innerH,
    [yMin, span, innerH]
  );
  const zeroY = toY(0);
  const slotW = n > 0 ? VIEW_W / n : VIEW_W;
  const centerX = useCallback((i) => (i + 0.5) * slotW, [slotW]);

  const handleMove = (event) => {
    if (!n || !surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? timeline[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  const axisIndices = useMemo(() => {
    if (n <= 1) return n === 1 ? [0] : [];
    if (n <= 8) return timeline.map((_, i) => i);
    const out = [];
    for (let k = 0; k < 5; k += 1) out.push(Math.round((k / 4) * (n - 1)));
    return [...new Set(out)];
  }, [n, timeline]);

  if (!n) {
    return (
      <div className="oc-fin-chart-empty">
        <Activity size={15} />
        <span>No cash movement yet — the chart draws as money enters and leaves the vault.</span>
      </div>
    );
  }

  const barW = Math.min(slotW * 0.3, 14);

  return (
    <div
      ref={surfaceRef}
      className="oc-fin-chart-surface"
      onPointerMove={handleMove}
      onPointerLeave={() => setHoverIndex(null)}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="oc-fin-chart-svg"
        role="img"
        aria-label="Treasury cashflow chart"
      >
        {[yMax, (yMax + yMin) / 2, yMin, ...(yMin < 0 && yMax > 0 ? [0] : [])].map(
          (level, i) => (
            <line
              key={`${level}-${i}`}
              x1={0}
              x2={VIEW_W}
              y1={toY(level)}
              y2={toY(level)}
              className={`oc-fin-grid${level === 0 ? " oc-fin-grid-zero" : ""}`}
              vectorEffect="non-scaling-stroke"
            />
          )
        )}

        {timeline.map((p, i) => {
          const cx = centerX(i);
          const dim = hoverIndex != null && hoverIndex !== i;
          return (
            <g key={p.date} opacity={dim ? 0.45 : 1}>
              {p.inflow > 0 ? (
                <rect
                  x={cx - barW - 1}
                  y={toY(p.inflow)}
                  width={barW}
                  height={Math.max(zeroY - toY(p.inflow), 1.5)}
                  rx={2}
                  className="oc-fin-bar-in"
                />
              ) : null}
              {p.outflow > 0 ? (
                <rect
                  x={cx + 1}
                  y={zeroY}
                  width={barW}
                  height={Math.max(toY(-p.outflow) - zeroY, 1.5)}
                  rx={2}
                  className="oc-fin-bar-out"
                />
              ) : null}
            </g>
          );
        })}

        {hover ? (
          <line
            x1={hoverXFrac * VIEW_W}
            x2={hoverXFrac * VIEW_W}
            y1={PAD_TOP}
            y2={VIEW_H - PAD_BOTTOM}
            className="oc-fin-crosshair"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      <div className="oc-fin-yaxis" aria-hidden>
        <span>{compact(yMax)}</span>
        {yMin < 0 && yMax > 0 ? <span>0</span> : null}
        <span>{compact(yMin)}</span>
      </div>

      <div className="oc-fin-xaxis" aria-hidden>
        {axisIndices.map((i) => (
          <span key={timeline[i]?.date || i} style={{ left: `${((i + 0.5) / n) * 100}%` }}>
            {dayLabel(timeline[i]?.date)}
          </span>
        ))}
      </div>

      {hover ? (
        <div
          className={`oc-fin-tooltip tabular-nums${hoverXFrac > 0.7 ? " oc-fin-tooltip-left" : ""}`}
          style={{ left: `${hoverXFrac * 100}%` }}
        >
          <em>{dayLabel(hover.date)}</em>
          <span className="oc-fin-up">In {compact(hover.inflow)}</span>
          <span className="oc-fin-down">Out {compact(hover.outflow)}</span>
          <strong
            className={hover.net > 0 ? "oc-fin-up" : hover.net < 0 ? "oc-fin-down" : ""}
          >
            Net {compact(hover.net)}
          </strong>
          {hover.internal > 0 ? (
            <span className="oc-fin-blue">Internal {compact(hover.internal)}</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/* ─────────────────────── Ledger explorer ─────────────────────── */

const LedgerRow = ({ entry }) => {
  const meta = LEDGER_TONE[entry.type] || { sign: "", tone: "muted" };
  const internal = INTERNAL_TYPES.has(entry.type);
  const Icon = LEDGER_ICONS[entry.type] || Coins;
  const iconTone = internal ? "" : meta.tone === "credit" ? "credit" : meta.tone === "debit" ? "debit" : "";
  const noteBits = [entry.note, entry.reference && !entry.note ? entry.reference : null].filter(
    Boolean
  );

  return (
    <li className="oc-fin-ledger-row">
      <span className={`oc-fin-ledger-icon${iconTone ? ` oc-fin-ledger-icon-${iconTone}` : ""}`}>
        <Icon size={12} />
      </span>
      <div className="oc-fin-ledger-copy">
        <strong>
          {ledgerLabel(entry.type)}
          {internal ? " · internal" : ""}
        </strong>
        <span>
          {formatAppDateTime(entry.createdAt, {
            includeWeekday: false,
            includeSeconds: false,
          })}
          {noteBits.length ? ` · ${noteBits.join(" · ")}` : ""}
        </span>
      </div>
      <em
        className={`oc-fin-ledger-amount tabular-nums oc-fin-ledger-amount-${
          internal ? "muted" : meta.tone
        }`}
      >
        {meta.sign}
        {formatCash(entry.amount, { compact: true })}
      </em>
    </li>
  );
};

/* ─────────────────────── Board ─────────────────────── */

/**
 * Treasury finance desk: vault hero, daily cashflow chart, and a paginated
 * ledger explorer backed by GET /api/bet/:houseId/treasury.
 * `actions` renders between the hero and the chart (deposit/withdraw slot).
 */
const TreasuryVaultBoard = ({ houseId, dashboard, actions }) => {
  const dispatch = useDispatch();
  const {
    treasuryDetail,
    treasuryDetailHouseId,
    treasuryDetailStatus,
    treasuryDetailError,
    treasuryLedger,
    treasuryLedgerPagination,
    treasuryFlowEntries,
    loadingMoreTreasuryLedger,
  } = useSelector((s) => s.operatorConsole);

  const [filterId, setFilterId] = useState("all");
  const filter = LEDGER_FILTERS.find((f) => f.id === filterId) || LEDGER_FILTERS[0];

  useEffect(() => {
    if (!houseId) return;
    setFilterId("all");
    dispatch(fetchHouseTreasury({ houseId, page: 1, limit: 50 }));
  }, [dispatch, houseId]);

  const detailReady =
    treasuryDetail && String(treasuryDetailHouseId) === String(houseId);
  const failed = treasuryDetailStatus === "failed" && !detailReady;
  const loading = treasuryDetailStatus === "loading" && !detailReady;

  const summary = detailReady ? treasuryDetail : dashboard?.treasury || {};
  const recentLedger = dashboard?.recentLedger;
  const fallbackLedger = useMemo(() => recentLedger || [], [recentLedger]);

  const flowEntries = detailReady ? treasuryFlowEntries : fallbackLedger;

  const { timeline, flowWindow } = useMemo(() => {
    const byDay = new Map();
    let inflow = 0;
    let outflow = 0;
    let internal = 0;
    for (const entry of flowEntries) {
      const amount = Math.abs(Number(entry.amount) || 0);
      const key = String(entry.createdAt || "").slice(0, 10);
      if (!key) continue;
      if (!byDay.has(key)) {
        byDay.set(key, { date: key, inflow: 0, outflow: 0, internal: 0, net: 0 });
      }
      const day = byDay.get(key);
      if (MONEY_IN_TYPES.has(entry.type)) {
        day.inflow += amount;
        inflow += amount;
      } else if (MONEY_OUT_TYPES.has(entry.type)) {
        day.outflow += amount;
        outflow += amount;
      } else if (INTERNAL_TYPES.has(entry.type)) {
        day.internal += amount;
        internal += amount;
      }
    }
    const days = [...byDay.values()]
      .map((d) => ({ ...d, net: d.inflow - d.outflow }))
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .slice(-21);
    const active = days.filter((d) => d.inflow > 0 || d.outflow > 0 || d.internal > 0);
    return {
      timeline: active,
      flowWindow: flowEntries.length
        ? {
            inflow,
            outflow,
            internal,
            net: inflow - outflow,
            spanLabel: `Last ${flowEntries.length} ledger entries`,
          }
        : null,
    };
  }, [flowEntries]);

  const runFilter = (next) => {
    setFilterId(next.id);
    if (!houseId) return;
    dispatch(
      fetchHouseTreasury({
        houseId,
        page: 1,
        limit: next.type ? 25 : 50,
        type: next.type || undefined,
      })
    );
  };

  const loadMore = () => {
    if (!houseId || !treasuryLedgerPagination?.hasNextPage) return;
    dispatch(
      fetchHouseTreasury({
        houseId,
        page: (treasuryLedgerPagination.page || 1) + 1,
        limit: treasuryLedgerPagination.limit || 25,
        type: filter.type || undefined,
        append: true,
      })
    );
  };

  const explorerEntries = useMemo(() => {
    const rows = detailReady ? treasuryLedger : fallbackLedger;
    if (filter.clientTypes) {
      return rows.filter((e) => filter.clientTypes.has(e.type));
    }
    return rows;
  }, [detailReady, treasuryLedger, fallbackLedger, filter]);

  const totalCount = treasuryLedgerPagination?.totalCount;

  return (
    <div className="oc-fin">
      <VaultHero summary={summary} flowWindow={flowWindow} />

      {actions}

      {/* ── Cashflow ── */}
      <section className="oc-fin-card">
        <header className="oc-fin-card-head">
          <div>
            <span className="oc-fin-kicker">Cashflow</span>
            <p className="oc-fin-caption">
              Money entering vs leaving the vault, day by day
            </p>
          </div>
        </header>

        {loading ? (
          <div className="oc-fin-chart-empty">
            <Loader2 size={16} className="oc-fin-spin" />
          </div>
        ) : failed && !flowEntries.length ? (
          <div className="oc-fin-chart-empty">
            <CloudOff size={15} />
            <span>{treasuryDetailError || "Could not load treasury detail."}</span>
            <button
              type="button"
              className="oc-fin-retry"
              onClick={() => dispatch(fetchHouseTreasury({ houseId, page: 1, limit: 50 }))}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <CashflowChart timeline={timeline} />
            <div className="oc-fin-chart-foot">
              <div className="oc-fin-chart-legend">
                <span className="oc-fin-comp-key">
                  <span className="oc-fin-comp-dot oc-fin-comp-dot-available" aria-hidden />
                  Money in
                </span>
                <span className="oc-fin-comp-key">
                  <span className="oc-fin-comp-dot oc-fin-comp-dot-locked" aria-hidden />
                  Money out
                </span>
              </div>
              <span className="oc-fin-threshold-hint">
                Liability locks and releases are internal moves — excluded from in/out
              </span>
            </div>
          </>
        )}
      </section>

      {/* ── Ledger explorer ── */}
      <section className="oc-fin-card">
        <header className="oc-fin-card-head">
          <div>
            <span className="oc-fin-kicker">Ledger explorer</span>
            <p className="oc-fin-caption">
              {detailReady && totalCount != null
                ? `${totalCount.toLocaleString()} movement${totalCount === 1 ? "" : "s"} on record`
                : "Every movement in and out of the vault"}
            </p>
          </div>
          <span className="oc-fin-ledger-icon" aria-hidden>
            <Receipt size={12} />
          </span>
        </header>

        {failed ? (
          <p className="oc-fin-fallback-note">
            <CloudOff size={13} />
            Full ledger unavailable — showing the {fallbackLedger.length} most recent
            movements from the dashboard.
          </p>
        ) : (
          <div className="oc-fin-chips" role="tablist" aria-label="Ledger type filter">
            {LEDGER_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filterId === f.id}
                className={`oc-fin-chip${filterId === f.id ? " oc-fin-chip-on" : ""}`}
                onClick={() => runFilter(f)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="oc-fin-chart-empty">
            <Loader2 size={16} className="oc-fin-spin" />
          </div>
        ) : explorerEntries.length === 0 ? (
          <p className="oc-fin-empty-copy">
            {filterId === "all"
              ? "No ledger entries yet — deposits and tickets will write the first lines."
              : `No ${filter.label.toLowerCase()} movements ${
                  filter.clientTypes ? "in the loaded pages" : "on record"
                }.`}
          </p>
        ) : (
          <ul className="oc-fin-ledger-list">
            {explorerEntries.map((entry, i) => (
              <LedgerRow
                key={entry.transactionRef || entry.reference || `${entry.type}-${entry.createdAt}-${i}`}
                entry={entry}
              />
            ))}
          </ul>
        )}

        {detailReady && treasuryLedgerPagination?.hasNextPage ? (
          <button
            type="button"
            className="oc-fin-loadmore"
            onClick={loadMore}
            disabled={loadingMoreTreasuryLedger}
          >
            {loadingMoreTreasuryLedger ? (
              <Loader2 size={13} className="oc-fin-spin" />
            ) : (
              <Clock3 size={13} />
            )}
            {loadingMoreTreasuryLedger ? "Loading…" : "Load older movements"}
          </button>
        ) : null}
      </section>
    </div>
  );
};

export default TreasuryVaultBoard;
